import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocalize } from "../i18n";
import {
  Box,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import { reverseFillVectors, setOverlayMode } from "../reducers/MapSlice";
import { PHONE_SCREEN, WIDE_SCREEN } from "../utils/layout";
import BaseMapToggle, { toggleSx } from "./BaseMapToggle";

// The map's display settings: what the field colours mean, which colouring
// is on, the base map and whether fields are filled.
export default function MapLegend({ disableSatellite, toggleLayer }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const localize = useLocalize();
  const fieldsLegends = useSelector((state) => state.mapState.fieldsLegends);
  const overlayMode = useSelector((state) => state.mapState.overlayMode);
  const fillVectors = useSelector((state) => state.mapState.fillVectors);
  const active = fieldsLegends.find((legend) => legend.key === overlayMode);

  return (
    <Box
      className="glass-panel"
      sx={{
        position: "fixed",
        bottom: 20,
        left: 20, // line up with the left panel
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        columnGap: 1.5,
        px: 1,
        py: 0.75,
        // Not enough room for one line
        // A grid, so the bar is only as wide as its longest line.
        [`@media not all and ${WIDE_SCREEN}`]: {
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gridTemplateAreas: '"modes tools" "colours colours"',
          rowGap: 0.5,
          "& .map-bar-modes": { gridArea: "modes", justifySelf: "start" },
          "& .map-bar-tools": { gridArea: "tools", justifySelf: "end" },
          "& .map-bar-colours": {
            gridArea: "colours",
            justifySelf: "start",
            pl: 1.25, // line up with the first mode's text
          },
          "& > .MuiDivider-root": { display: "none" },
        },
        // Phones: full width, above the bottom tab bar.
        [`@media ${PHONE_SCREEN}`]: {
          bottom: "80px",
          left: "12px",
          right: "12px",
          "& .map-bar-colours": { flexWrap: "wrap" },
        },
      }}
    >
      {active && (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={overlayMode}
            onChange={(_, mode) => mode && dispatch(setOverlayMode(mode))}
            aria-label={t("map.bar.colourBy")}
            className="map-bar-modes"
            sx={toggleSx}
          >
            {fieldsLegends.map((legend) => (
              <ToggleButton key={legend.key} value={legend.key}>
                {localize(legend, "name")}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          <Box
            className="map-bar-colours"
            sx={{
              display: "flex",
              columnGap: 1.5,
              rowGap: 0.25,
            }}
          >
            {active.legends.map((item) => (
              <Box
                key={item.label_en}
                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
                  }}
                />
                <Typography sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {localize(item, "label")}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider orientation="vertical" flexItem />
        </>
      )}

      <Box
        className="map-bar-tools"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <BaseMapToggle
          value={toggleLayer}
          disableSatellite={disableSatellite}
        />
        <Tooltip
          title={
            fillVectors ? t("map.bar.showOutlines") : t("map.bar.fillFields")
          }
        >
          <IconButton
            size="small"
            aria-label={t("map.bar.fillFields")}
            aria-pressed={fillVectors}
            onClick={() => dispatch(reverseFillVectors())}
            sx={[
              {
                p: "6px",
                borderRadius: "10px",
                border: "1px solid rgba(0,0,0,0.12)",
              },
              fillVectors && {
                bgcolor: "primary.main",
                color: "#fff",
                "&:hover": { bgcolor: "primary.dark" },
              },
            ]}
          >
            <FormatColorFillIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
